import { useState, useCallback, useEffect, useRef } from 'react';
import { FormField } from '../types';
import { isBlacklistedField } from '../utils/fieldBlacklist';

interface UsePartialDataCaptureOptions {
  formId: string;
  formData: Record<string, any>;
  formFields: FormField[];
  enabled?: boolean;
  apiUrl?: string;
  debounceMs?: number;
  maxFieldLength?: number;
}

interface PartialDataState {
  partialData: Record<string, any>;
  captureFieldData: (fieldId: string, value: any) => void;
  setupAbandonmentDetection: () => void;
  getCurrentPartialData: () => Record<string, any>;
  calculateProgress: () => number;
}

/**
 * Hook to capture partial form data for abandonment recovery
 * 
 * Features:
 * - Captures field data in real-time (onChange, onBlur)
 * - Stores in memory and localStorage backup
 * - Detects abandonment with multiple methods (visibilitychange, pagehide, beforeunload)
 * - Filters sensitive fields (passwords, credit cards, etc.)
 * - Sends data reliably using fetch keepalive or sendBeacon
 */
export function usePartialDataCapture({
  formId,
  formData,
  formFields,
  enabled = true,
  apiUrl,
  debounceMs = 10000, // 10 seconds
  maxFieldLength = 5000,
}: UsePartialDataCaptureOptions): PartialDataState {
  const [partialData, setPartialData] = useState<Record<string, any>>({});
  const lastSyncTimeRef = useRef<number>(Date.now());
  const periodicSyncIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const abandonmentListenersRef = useRef<Array<() => void>>([]);
  const sessionStartTimeRef = useRef<number>(Date.now());

  const getApiUrl = useCallback(() => {
    if (apiUrl) return apiUrl;
    
    if (typeof window !== 'undefined' && (window as any).HEXAFORMS_API_URL) {
      return (window as any).HEXAFORMS_API_URL;
    }
    
    return 'https://80usneael9.execute-api.eu-west-1.amazonaws.com/dev';
  }, [apiUrl]);

  // Get session ID from sessionStorage (same as useFieldTracking)
  const getSessionId = useCallback(() => {
    if (typeof window === 'undefined' || !window.sessionStorage) {
      return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    }

    const stored = sessionStorage.getItem(`hexaforms_session_${formId}`);
    if (stored) {
      return stored;
    }

    const newSessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem(`hexaforms_session_${formId}`, newSessionId);
    return newSessionId;
  }, [formId]);

  // Save partial data to localStorage as backup
  const savePartialDataToLocalStorage = useCallback((data: Record<string, any>) => {
    if (!enabled || typeof window === 'undefined' || !window.localStorage) {
      return;
    }

    try {
      const storageKey = `hexaforms_partial_${formId}`;
      localStorage.setItem(
        storageKey,
        JSON.stringify({
          sessionId: getSessionId(),
          formId,
          timestamp: Date.now(),
          data,
        })
      );
    } catch (error) {
      // localStorage may be disabled or full
      if ((window as any).HEXAFORMS_DEBUG) {
        console.warn('[HexaForms] Could not save partial data to localStorage:', error);
      }
    }
  }, [enabled, formId, getSessionId]);

  // Capture field data
  const captureFieldData = useCallback(
    (fieldId: string, value: any) => {
      if (!enabled) return;

      // Find field config
      const field = formFields.find((f) => f.fieldId === fieldId);
      if (!field) return;

      // Check if field is blacklisted
      if (isBlacklistedField(field)) {
        if ((window as any).HEXAFORMS_DEBUG) {
          console.log(`[HexaForms] Skipping blacklisted field: ${fieldId}`);
        }
        return;
      }

      // Don't capture empty values
      if (!value || (typeof value === 'string' && !value.trim())) {
        setPartialData((prev) => {
          const newData = { ...prev };
          if (newData[fieldId]) {
            delete newData[fieldId];
          }
          return newData;
        });
        return;
      }

      // Truncate if too long
      let finalValue = value;
      if (typeof value === 'string' && value.length > maxFieldLength) {
        finalValue = value.substring(0, maxFieldLength) + '...';
      }

      // Update state
      setPartialData((prev) => {
        const newData = { ...prev, [fieldId]: finalValue };
        savePartialDataToLocalStorage(newData);
        return newData;
      });

      if ((window as any).HEXAFORMS_DEBUG) {
        console.log(
          `[HexaForms] Captured field: ${fieldId} (${String(finalValue).length} chars)`
        );
      }
    },
    [enabled, formFields, maxFieldLength, savePartialDataToLocalStorage]
  );

  // Calculate progress percentage
  const calculateProgress = useCallback(() => {
    if (formFields.length === 0) return 0;

    const completedFields = formFields.filter((field) => {
      if (isBlacklistedField(field)) return false;
      const value = formData[field.fieldId];
      return value !== undefined && value !== null && value !== '';
    }).length;

    return Math.round((completedFields / formFields.length) * 100);
  }, [formFields, formData]);

  // Get current partial data (merge formData with partialData)
  const getCurrentPartialData = useCallback(() => {
    const current: Record<string, any> = {};

    formFields.forEach((field) => {
      if (isBlacklistedField(field)) return;

      const fieldId = field.fieldId;
      const value = formData[fieldId] ?? partialData[fieldId];

      if (value !== undefined && value !== null && value !== '') {
        current[fieldId] = value;
      }
    });

    return current;
  }, [formFields, formData, partialData]);

  // Send partial data to backend (periodic sync - less urgent)
  const syncPartialDataToBackend = useCallback(async () => {
    if (!enabled) return;

    const currentData = getCurrentPartialData();
    if (Object.keys(currentData).length === 0) return;

    try {
      const finalApiUrl = getApiUrl();
      const sessionId = getSessionId();

      await fetch(`${finalApiUrl}/analytics/track/funnel/${formId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sessionId,
          eventType: 'session_in_progress',
          startedAt: new Date(sessionStartTimeRef.current).toISOString(),
          progressPercentage: calculateProgress(),
          partialData: currentData,
        }),
      });

      lastSyncTimeRef.current = Date.now();
    } catch (error) {
      // Silently fail (non-critical)
      if ((window as any).HEXAFORMS_DEBUG) {
        console.debug('[HexaForms] Periodic sync failed:', error);
      }
    }
  }, [enabled, formId, getApiUrl, getSessionId, getCurrentPartialData, calculateProgress]);

  // Send abandonment data reliably
  const sendAbandonmentData = useCallback(
    async (method: 'visibilitychange' | 'pagehide' | 'beforeunload' | 'periodic' = 'periodic') => {
      if (!enabled) return;

      const currentData = getCurrentPartialData();
      if (Object.keys(currentData).length === 0) {
        if ((window as any).HEXAFORMS_DEBUG) {
          console.log('[HexaForms] No partial data to send');
        }
        return;
      }

      const finalApiUrl = getApiUrl();
      const sessionId = getSessionId();
      const progress = calculateProgress();

      const payload = {
        sessionId,
        eventType: 'session_abandoned',
        startedAt: new Date(sessionStartTimeRef.current).toISOString(),
        abandonedAt: new Date().toISOString(),
        progressPercentage: progress,
        totalDuration: Date.now() - sessionStartTimeRef.current,
        partialData: currentData,
      };

      // Try fetch with keepalive first (more flexible)
      if (method !== 'beforeunload') {
        try {
          await fetch(`${finalApiUrl}/analytics/track/funnel/${formId}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
            keepalive: true, // Critical: keeps connection alive during page unload
          });

          lastSyncTimeRef.current = Date.now();
          if ((window as any).HEXAFORMS_DEBUG) {
            console.log(`[HexaForms] Partial data sent successfully (${method})`);
          }
          return;
        } catch (error) {
          if ((window as any).HEXAFORMS_DEBUG) {
            console.debug('[HexaForms] Fetch failed, trying sendBeacon:', error);
          }
        }
      }

      // Fallback: use sendBeacon (more reliable in beforeunload)
      if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
        const blob = new Blob([JSON.stringify(payload)], {
          type: 'application/json',
        });
        const success = navigator.sendBeacon(
          `${finalApiUrl}/analytics/track/funnel/${formId}`,
          blob
        );

        if (success) {
          lastSyncTimeRef.current = Date.now();
          if ((window as any).HEXAFORMS_DEBUG) {
            console.log(`[HexaForms] sendBeacon succeeded (${method})`);
          }
        } else {
          if ((window as any).HEXAFORMS_DEBUG) {
            console.warn(`[HexaForms] sendBeacon failed (${method})`);
          }
        }
      }
    },
    [enabled, formId, getApiUrl, getSessionId, getCurrentPartialData, calculateProgress]
  );

  // Setup abandonment detection with multiple methods
  const setupAbandonmentDetection = useCallback(() => {
    if (!enabled || typeof window === 'undefined') return;

    // Cleanup existing listeners
    abandonmentListenersRef.current.forEach((cleanup) => cleanup());
    abandonmentListenersRef.current = [];

    // METHOD 1: visibilitychange (MOST RELIABLE)
    // Detects when user switches tabs or minimizes browser
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        sendAbandonmentData('visibilitychange');
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    abandonmentListenersRef.current.push(() => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    });

    // METHOD 2: pagehide (FOR MOBILE)
    // More reliable than beforeunload in Safari iOS and Chrome Android
    const handlePageHide = () => {
      sendAbandonmentData('pagehide');
    };
    window.addEventListener('pagehide', handlePageHide);
    abandonmentListenersRef.current.push(() => {
      window.removeEventListener('pagehide', handlePageHide);
    });

    // METHOD 3: beforeunload (FALLBACK DESKTOP)
    // Only as last resort
    const handleBeforeUnload = () => {
      sendAbandonmentData('beforeunload');
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    abandonmentListenersRef.current.push(() => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    });

    // METHOD 4: Periodic sync (BACKUP)
    // Sync every debounceMs as backup
    if (periodicSyncIntervalRef.current) {
      clearInterval(periodicSyncIntervalRef.current);
    }

    periodicSyncIntervalRef.current = setInterval(() => {
      const timeSinceLastSync = Date.now() - lastSyncTimeRef.current;

      if (
        timeSinceLastSync >= debounceMs &&
        Object.keys(getCurrentPartialData()).length > 0
      ) {
        syncPartialDataToBackend();
      }
    }, debounceMs);

    if ((window as any).HEXAFORMS_DEBUG) {
      console.log('[HexaForms] Abandonment detection enabled (4 methods)');
    }
  }, [enabled, debounceMs, sendAbandonmentData, syncPartialDataToBackend, getCurrentPartialData]);

  // Initialize session start time
  useEffect(() => {
    sessionStartTimeRef.current = Date.now();
  }, []);

  // Setup abandonment detection on mount
  useEffect(() => {
    if (enabled) {
      setupAbandonmentDetection();
    }

    return () => {
      // Cleanup listeners
      abandonmentListenersRef.current.forEach((cleanup) => cleanup());
      abandonmentListenersRef.current = [];

      // Cleanup interval
      if (periodicSyncIntervalRef.current) {
        clearInterval(periodicSyncIntervalRef.current);
        periodicSyncIntervalRef.current = null;
      }

      // Send final abandonment data on unmount
      if (enabled) {
        sendAbandonmentData('periodic');
      }
    };
  }, [enabled, setupAbandonmentDetection, sendAbandonmentData]);

  // Sync partial data when formData changes (debounced)
  useEffect(() => {
    if (!enabled) return;

    // Update partialData from formData (for fields that changed)
    const newPartialData: Record<string, any> = { ...partialData };

    formFields.forEach((field) => {
      if (isBlacklistedField(field)) return;

      const fieldId = field.fieldId;
      const value = formData[fieldId];

      if (value !== undefined && value !== null && value !== '') {
        newPartialData[fieldId] = value;
      } else if (newPartialData[fieldId]) {
        // Field was cleared
        delete newPartialData[fieldId];
      }
    });

    if (JSON.stringify(newPartialData) !== JSON.stringify(partialData)) {
      setPartialData(newPartialData);
      savePartialDataToLocalStorage(newPartialData);
    }
  }, [formData, formFields, enabled, partialData, savePartialDataToLocalStorage]);

  return {
    partialData: getCurrentPartialData(),
    captureFieldData,
    setupAbandonmentDetection,
    getCurrentPartialData,
    calculateProgress,
  };
}
